/**
 * SIDEBAR NAVIGATION - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Features:
 * - Liquid Glass with lensing and refraction
 * - Color reflection from surrounding content
 * - Adaptive tinting based on active state
 * - Fluid morphing animations with spring physics
 * - Specular highlights and ambient glow
 * - Animated active indicator (pill morphing)
 * - Staggered nav item entrance
 */

import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Home, Users, UserCircle, DollarSign, FileText, Settings, LogOut, ChevronRight, Wallet, PiggyBank, RefreshCw, User, CreditCard, Lock, CalendarCheck } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { cn } from '../../lib/utils'

// Navigation items - some are admin-only
const getNavigation = (isAdmin: boolean) => {
  const items = [
    { name: 'Dashboard', href: '/', icon: Home, color: 'blue', adminOnly: false },
    { name: 'My Portal', href: '/my-portal', icon: User, color: 'purple', adminOnly: false },
    { name: 'Advances', href: '/advances', icon: CreditCard, color: 'orange', adminOnly: false },
    { name: 'Partners', href: '/partners', icon: Users, color: 'purple', adminOnly: true },
    { name: 'Clients', href: '/clients', icon: UserCircle, color: 'green', adminOnly: true },
    { name: 'Transactions', href: '/transactions', icon: DollarSign, color: 'orange', adminOnly: true },
    { name: 'Pool Balance', href: '/pool', icon: Wallet, color: 'cyan', adminOnly: true },
    { name: 'Distributions', href: '/distributions', icon: PiggyBank, color: 'pink', adminOnly: true },
    { name: 'Reserves', href: '/reserves', icon: Lock, color: 'emerald', adminOnly: true },
    { name: 'Monthly Closing', href: '/monthly-closing', icon: CalendarCheck, color: 'amber', adminOnly: true },
    { name: 'Wise Sync', href: '/wise', icon: RefreshCw, color: 'green', adminOnly: true },
    { name: 'Reports', href: '/reports', icon: FileText, color: 'indigo', adminOnly: true },
    { name: 'Settings', href: '/settings', icon: Settings, color: 'teal', adminOnly: false },
  ]
  return items.filter(item => !item.adminOnly || isAdmin)
}

export function Sidebar() {
  const { user, logout } = useAuth()
  const location = useLocation()

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  // Navigation items for current user
  const navItems = getNavigation(user?.type === 'Admin')

  // Find active item index for animation
  const activeIndex = navItems.findIndex(item =>
    item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
  )

  return (
    <motion.div
      className="flex flex-col h-full liquid-material-regular liquid-lens rounded-3xl overflow-hidden shadow-liquid-lg dark:shadow-dark-liquid-lg"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
    >
      {/* Logo Section */}
      <div className="px-6 py-5 border-b border-light-separator dark:border-dark-separator">
        <div className="flex items-center gap-3.5">
          {/* App Icon with Liquid Glass effect */}
          <div className="relative w-12 h-12 rounded-2xl overflow-hidden shadow-glass dark:shadow-dark-glass group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue via-blue-dark to-indigo animate-liquid-shimmer bg-[length:200%_200%]" />
            <div className="absolute inset-0 liquid-glass-specular" />
            <div className="relative w-full h-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          {/* App Name */}
          <div className="flex-1 min-w-0">
            <h1 className="text-headline font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight truncate">
              GHL Financial
            </h1>
            <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary font-medium tracking-tight">
              Partners Portal
            </p>
          </div>
        </div>
      </div>

      {/* Navigation with staggered animation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-glass relative">
        {navItems.map((item, index) => {
          const isActive = item.href === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.href)

          return (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: index * 0.03,
                type: "spring",
                damping: 25,
                stiffness: 300
              }}
            >
              <NavLink
                to={item.href}
                end={item.href === '/'}
                className="group relative flex items-center justify-between px-3.5 py-2.5 rounded-xl text-subheadline font-semibold"
              >
                {/* Animated background for active state */}
                {isActive && (
                  <motion.div
                    layoutId="activeNavBg"
                    className="absolute inset-0 bg-blue-glass dark:bg-blue-glass rounded-xl shadow-glass-sm"
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  />
                )}

                <div className={cn(
                  "flex items-center gap-3 relative z-10 transition-colors duration-fast",
                  isActive
                    ? "text-blue dark:text-blue-dark"
                    : "text-light-text-secondary dark:text-dark-text-secondary group-hover:text-light-text-primary dark:group-hover:text-dark-text-primary"
                )}>
                  <motion.div
                    className={cn(
                      "relative p-1.5 rounded-lg transition-all duration-base",
                      isActive
                        ? "bg-blue dark:bg-blue-dark shadow-md"
                        : "bg-light-bg-tertiary/50 dark:bg-dark-bg-tertiary/50 group-hover:bg-light-bg-tertiary dark:group-hover:bg-dark-bg-tertiary"
                    )}
                    whileHover={!isActive ? { scale: 1.1 } : undefined}
                    whileTap={{ scale: 0.95 }}
                  >
                    <item.icon
                      className={cn(
                        "w-[18px] h-[18px] transition-colors duration-base",
                        isActive
                          ? "text-white"
                          : "text-light-text-tertiary dark:text-dark-text-tertiary group-hover:text-light-text-secondary dark:group-hover:text-dark-text-secondary"
                      )}
                      strokeWidth={2.5}
                    />
                  </motion.div>
                  <span className="tracking-tight">{item.name}</span>
                </div>

                {/* Active indicator pill on left edge */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      className="absolute left-0 top-1/2 w-1 h-8 bg-gradient-to-b from-blue via-blue-dark to-blue rounded-r-full shadow-lg"
                      initial={{ opacity: 0, x: -4, y: '-50%' }}
                      animate={{ opacity: 1, x: 0, y: '-50%' }}
                      exit={{ opacity: 0, x: -4 }}
                      transition={{ type: "spring", damping: 20, stiffness: 300 }}
                    />
                  )}
                </AnimatePresence>

                {/* Hover chevron */}
                <motion.div
                  className="relative z-10"
                  initial={{ opacity: 0, x: -5 }}
                  animate={{
                    opacity: isActive ? 0 : undefined,
                    x: 0
                  }}
                  whileHover={{ opacity: 0.5, x: 2 }}
                  transition={{ duration: 0.15 }}
                >
                  {!isActive && (
                    <ChevronRight
                      className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary opacity-0 group-hover:opacity-50 transition-opacity duration-fast"
                      strokeWidth={2.5}
                    />
                  )}
                </motion.div>
              </NavLink>
            </motion.div>
          )
        })}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-light-separator dark:border-dark-separator space-y-2">
        {/* User Info with Liquid Glass */}
        <motion.div
          className="liquid-material-clear rounded-xl p-3 cursor-pointer group"
          whileHover={{
            scale: 1.02,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", damping: 20, stiffness: 400 }}
        >
          <div className="flex items-center gap-3">
            {/* Avatar with gradient and glass effect */}
            <motion.div
              className="relative w-10 h-10 rounded-full overflow-hidden shadow-glass dark:shadow-dark-glass"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", damping: 15, stiffness: 300 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple via-purple-dark to-pink animate-liquid-shimmer bg-[length:200%_200%]" />
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
              <div className="relative w-full h-full flex items-center justify-center">
                <span className="text-white font-bold text-caption1 tracking-tight drop-shadow-sm">
                  {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </span>
              </div>
            </motion.div>

            {/* User Details */}
            <div className="flex-1 min-w-0">
              <p className="text-subheadline font-semibold text-light-text-primary dark:text-dark-text-primary truncate tracking-tight">
                {user?.name}
              </p>
              <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary font-medium tracking-tight capitalize">
                {user?.type}
              </p>
            </div>

            {/* Expand indicator */}
            <motion.div
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 0 }}
              whileHover={{ opacity: 1, x: 0 }}
              className="group-hover:opacity-100"
            >
              <ChevronRight className="w-4 h-4 text-light-text-quaternary dark:text-dark-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity duration-fast" strokeWidth={2.5} />
            </motion.div>
          </div>
        </motion.div>

        {/* Logout Button with Liquid Glass destructive variant */}
        <motion.button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-xl text-subheadline font-semibold text-red dark:text-red-dark group overflow-hidden relative"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", damping: 20, stiffness: 400 }}
        >
          {/* Hover background */}
          <motion.div
            className="absolute inset-0 bg-red-glass dark:bg-red-glass rounded-xl"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          />

          <motion.div
            className="p-1.5 rounded-lg bg-red/10 dark:bg-red-glass group-hover:bg-red dark:group-hover:bg-red-dark transition-all duration-base relative z-10"
            whileHover={{ scale: 1.1 }}
          >
            <LogOut className="w-[18px] h-[18px] text-red dark:text-red-dark group-hover:text-white transition-colors duration-base" strokeWidth={2.5} />
          </motion.div>
          <span className="tracking-tight relative z-10">Sign Out</span>
        </motion.button>
      </div>
    </motion.div>
  )
}
